import React from 'react';
import { BackgroundImage, Center, Grid, Overlay, Paper, PaperProps, Container } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { PasswordChangeForm } from './PasswordChangeForm';
import Login from './Login';
import SignUp from './SignUp';
import Verification from './VerifyEmail';
import Waitlist from './Waitlist';
import {RequestForgottenPassword} from './RequestForgottenPassword';
import {ResetForgottenPassword} from "./ResetForgottenPassword";
import { useNavigate, useLocation } from "react-router-dom";
import { handleSignUp, signIn, verifyEmailWithCode, resetPassword } from "../../api/Auth";
import logger from '../../utils/logger';


const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

export interface AuthenticatedUserSession {
    sub: string;
    token: string;
    expiresIn: number;
    userAttributes: {
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
    }
}

interface AuthenticationTitleProps extends PaperProps {
    onLoginSuccess: () => void;
}

export function AuthenticationTitle({ onLoginSuccess, ...props }: AuthenticationTitleProps) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;

    const handlePasswordChangeSubmit = async (firstName: string, lastName: string, newPassword: string) => {
        try {
            // Implement your password change logic here
            navigate('/auth/login');
        } catch (error) {
            console.error('Password change error:', error);
        }
    };

    const handlePasswordReset = async (firstName: string, lastName: string, newPassword: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const isForced = searchParams.get('forced') === 'true';
        const token = searchParams.get('token');

        if (!isForced && !token) {
            navigate('/auth/login?error=true&message=Invalid password reset request');
            return;
        }

        try {
            const result = await resetPassword(token || '', newPassword, isForced);
            if (result.success) {
                navigate('/auth/login?passwordReset=true');
            } else {
                navigate('/auth/login?error=true&message=' + (result.error || 'Password reset failed'));
            }
        } catch (error) {
            navigate('/auth/login?error=true&message=An unexpected error occurred');
        }
    };

    const renderAuthComponent = () => {
        logger.log('Current authentication path:', currentPath);
        switch (currentPath) {
            case '/login':
                return <Login
                    onLoginSuccess={onLoginSuccess}
                    onRegisterClick={() => navigate('/signup')}
                    onForgotPasswordClick={() => navigate('/forgot-password')}
                />;
            case '/signup':
                return <SignUp
                    onLoginClick={() => navigate('/login')}
                />;
            case '/verify-email':
                return <Verification/>; // todo: pass in onVerificationSuccess
            case '/reset-password':
                return <PasswordChangeForm onSubmit={handlePasswordReset} />;
           
            case '/change-password':
                return <PasswordChangeForm onSubmit={handlePasswordReset} />;
            case '/waitlist':
                return <Waitlist onLoginClick={() => navigate('/login')} />;
            case '/forgot-password':
                return <RequestForgottenPassword />;
            case '/reset-forgotten-password':
                logger.log('Reset forgotten password component rendered');
                return <ResetForgottenPassword />;
            default:
                return <Login
                    onLoginSuccess={onLoginSuccess}
                    onRegisterClick={() => navigate('/signup')}
                    onForgotPasswordClick={() => navigate('/forgot-password')}
                />;
        }
    };

    const AuthContainer = ({ children }: { children: React.ReactNode }) => (
        <Center style={{ height: '100%' }}>
            <Paper
                radius="md"
                p="xl"
                withBorder
                {...props}
                style={{
                    width: '90%',
                    maxWidth: isMobile ? '600px' : '750px',
                    zIndex: 2
                }}
            >
                {children}
            </Paper>
        </Center>
    );

    return (
        <Grid>
            <script src="https://accounts.google.com/gsi/client" async></script>
            <BackgroundImage
                src="https://images.unsplash.com/photo-1453828423292-392a660a502f?q=80&w=3264&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                radius="sm"
                style={{ width: '100%', height: '101vh' }}
            >
                <Overlay color="gray" opacity={0.5} zIndex={1} />
                <AuthContainer>
                    {renderAuthComponent()}
                </AuthContainer>
            </BackgroundImage>
        </Grid>
    );
}